import { useCallback, useEffect, useMemo, useState } from 'react';
import { isNotBlank } from '../../../Components/CoreLib/library';
import { StepExecutionDataDto, StepExecutionDto } from '../../../dtos';
import { useGetPlayExecutionByPlayIdReservedNamesQuery } from '../../../store/generated/generatedApi';
import { useStepDataValidator } from '../../Plays/Hooks';
import { StepExecutionAssignableJobFunctionDto } from '../../../dtos/generated/StepExecutionAssignableJobFunctionDto';

export interface IStepFormValues {
    stepData: StepExecutionDataDto[];
}

export function useCompleteStepForm(initialStepExecution: StepExecutionDto) {
    const [formStepData, setFormStepData] = useState<StepExecutionDataDto[]>([]);
    const [formAdditionalStepData, setFormAdditionalStepData] = useState<StepExecutionDataDto[]>([]);
    const [formJobFunctionAssignment, setFormJobFunctionAssignment] = useState<StepExecutionAssignableJobFunctionDto[]>([]);
    const {data: reservedDataLabels = [], isLoading: isLoadingReservedLabels} = useGetPlayExecutionByPlayIdReservedNamesQuery({ playId: initialStepExecution.playId });
    const { isStepDataValid } = useStepDataValidator();

    useEffect(() => {
        setFormStepData(initialStepExecution.stepData);
        setFormAdditionalStepData([]);
        setFormJobFunctionAssignment(initialStepExecution.assignableJobFunctions);
    }, [initialStepExecution.stepData, initialStepExecution.assignableJobFunctions]);

    const validateAdditionalStepLabel = useCallback((stepData: StepExecutionDataDto) => {
        const {isValid: isNameValid} = isNotBlank(stepData.name)
        // make sure name is not blank
        if (!isNameValid) {
            return 'Label is required for manually added data'
        }

        // make sure name is unique on page
        const allStepNames = [...formStepData, ...formAdditionalStepData].map(x => x.name);
        if (allStepNames.filter(x => (x ?? '').toUpperCase().trim() === (stepData.name ?? '').toUpperCase().trim()).length > 1) {
            return 'Label must be unique';
        }

        // make sure name is not one of the existing reserved labels
        if (reservedDataLabels.map(x => x.toUpperCase().trim()).includes((stepData.name ?? '').toUpperCase().trim())) {
            return 'This label is reserved and cannot be used here';
        }

        return '';
    }, [formStepData, formAdditionalStepData, reservedDataLabels]);

    const isFormValid = useMemo(() => {
        const isEveryDataEntryValid = formStepData.every(sd => isStepDataValid(sd));
        const isEveryAdditionalDataEntryValid = formAdditionalStepData.every(asd => validateAdditionalStepLabel(asd) === '' && !!asd.value.toString().trim());
        return isEveryDataEntryValid && isEveryAdditionalDataEntryValid;
    }, [formStepData, formAdditionalStepData, isStepDataValid, validateAdditionalStepLabel]);

    const isFormDirty = () => {
        var isDirty = formStepData.some(x => x.value !== '');
        return isDirty;
    };

    const handleStepDataChange = (updatedData: StepExecutionDataDto[]) => {
        setFormStepData(updatedData);
    };

    const handleAdditionalStepDataChange = (updatedData: StepExecutionDataDto[]) => {
        setFormAdditionalStepData(updatedData);
    };

    return {
        isFormDirty,
        isFormValid,
        handleStepDataChange,
        formStepData,
        formAdditionalStepData,
        validateAdditionalStepLabel,
        handleAdditionalStepDataChange,
        formJobFunctionAssignment,
        setFormJobFunctionAssignment,
        isLoading: isLoadingReservedLabels
    };
}
